import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../util/routes';
import api from '../wrapper/API';
import { usePopup } from '../context/PopupContext';
import ListItem from '../list/ListItem';
import ListTextItem from '../list/ListTextItem';
import ListSymbols from '../list/ListSymbols';
import ListSymbol from '../list/ListSymbol';
import ParticipantGroupDot from '../participant-group/ParticipantGroupDot';
import Textfield from '../input/Textfield';
import SubmitButton from '../input/SubmitButton';
import Popup from '../popup/Popup';
import Icon from '../home/Icon';
import ContextMenu from '../context-menu/ContextMenu';
import ContextMenuItem from '../context-menu/ContextMenuItem';
import { useContextMenu } from '../context/ContextMenuContext';
import './PlayersList.css';

// Listenansicht aller Spieler. Client-seitige Suche nach Nachname/Vorname/E-Mail/Telefon.
// Der Punkt zeigt Gruppe + Rolle (participantGroup/role aus /Player/List; Orga/Admin überschreiben die Gruppenfarbe).
// Klick auf eine Zeile öffnet den bestehenden EditPlayerScreen (/users/:playerId).
function PlayersList() {
  const navigate = useNavigate();
  const { openPopup } = usePopup();
  const { contextMenuData } = useContextMenu();

  const [players, setPlayers] = useState([]);
  const [query, setQuery] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/Player/List')
      .then(res => {
        setPlayers(Array.isArray(res.data) ? res.data : []);
      })
      .catch(err => {
        setError(err.response?.data ?? 'Spielerliste konnte nicht geladen werden.');
        openPopup('playerslisterror', 1);
      });
  }, [openPopup]);

  const normalizedQuery = query.trim().toLowerCase();

  const filtered = useMemo(() => {
    if (!normalizedQuery) return players;
    const q = normalizedQuery;
    const qPhone = q.replace(/\s+/g, '');
    return players.filter(p => {
      if ((p.lastName ?? '').toLowerCase().includes(q)) return true;
      if ((p.firstName ?? '').toLowerCase().includes(q)) return true;
      if ((p.email ?? '').toLowerCase().includes(q)) return true;
      if (qPhone && (p.phone ?? '').replace(/\s+/g, '').toLowerCase().includes(qPhone)) return true;
      const full = `${p.lastName ?? ''} ${p.firstName ?? ''}`.toLowerCase();
      const fullReversed = `${p.firstName ?? ''} ${p.lastName ?? ''}`.toLowerCase();
      return full.includes(q) || fullReversed.includes(q);
    });
  }, [players, normalizedQuery]);

  const handleRowClick = (_event, playerId) => {
    navigate(ROUTES.user(playerId));
  };

  const handleAddClick = () => {
    navigate(ROUTES.user(0));
  };

  return (
    <React.Fragment>
      <div className={'playersListContent'}>
        <div className={'playersListSearchRow'}>
          <Textfield
            icon={'search'}
            placeholder={'Vorname, Nachname, E-Mail ...'}
            onChange={(e) => setQuery(e.target.value)}
            value={query}
          />
          <SubmitButton
            icon={'person_add'}
            onClick={handleAddClick}
            styletype={'smallround'}
            tooltip={'Neuen Spieler anlegen'}
          />
        </div>
        <div className={'playersListMeta'}>
          <span className={'playersListMetaCount'}>{filtered.length}</span>
          <span className={'playersListMetaLabel'}>
            {normalizedQuery ? (filtered.length === 1 ? 'Treffer' : 'Treffer') : (filtered.length === 1 ? 'Spieler' : 'Spieler')}
          </span>
        </div>
        {filtered.length === 0 && (
          <div className={'playersListEmpty'}>
            {players.length === 0 ? 'Keine Spieler vorhanden.' : 'Keine Treffer.'}
          </div>
        )}
        {filtered.map((p, index) => (
          <ListItem
            key={p.id}
            identifier={p.id}
            contextMenuIdentifier={'playerslist'}
            gridTemplateColumns={'auto 1fr auto'}
            index={Math.min(index, 20)}
            onClick={handleRowClick}
            className="userPlayerCard"
          >
            <ParticipantGroupDot row={p} />
            <ListTextItem type="primary">
              <div className="userNameCell">
                <span className="userNameLast">{p.lastName}</span>
                {p.firstName && <span className="userNameFirst">{p.firstName}</span>}
              </div>
            </ListTextItem>
            <ListSymbols>
              <ListSymbol icon={"note_stack"} visible={!!p.memo} type={2} />
            </ListSymbols>
            <ListTextItem type="meta">
              <div className="userContactStack">
                {p.email && (
                  <div className="userContactRow">
                    <Icon icon="mail" className="userContactIcon" />
                    <span>{p.email}</span>
                  </div>
                )}
                {p.phone && (
                  <div className="userContactRow">
                    <Icon icon="call" className="userContactIcon" />
                    <span>{p.phone}</span>
                  </div>
                )}
              </div>
            </ListTextItem>
          </ListItem>
        ))}
      </div>
      <Popup name='playerslisterror' confirmText='Ok' headerText='Error' type='error'>
        <p>{error}</p>
      </Popup>
      <ContextMenu type="playerslist">
        <ContextMenuItem icon="email" type={3} label="Mail senden" onClick={() => navigate(ROUTES.mailPlayer(contextMenuData.identifier))} />
      </ContextMenu>
    </React.Fragment>
  );
}

export default PlayersList;
